import _ from 'lodash';
import { useMemo, useRef } from 'react';
import { EMITTER_CONSTANTS, KEY_RECOIL } from '@/shared/config';
import { useDebounceEffect, useEmitter } from '@/shared/lib/customHooks';
import { cancelAllAPI } from '@/shared/lib/apiHelpers';
import { BULK_ACTION_ENUM, LOAD_BEAT_STATUS } from '@/shared/model';
import {
  calculateReviewedStatus,
  handleMarkReviewByCellOnly,
} from '@/widgets/beats/ui/reviewBeatsHelper';
import {
  STATUS_CHECKBOX,
  dataChartHeatMap,
} from '@/widgets/beats/ui/rrInterval/heatMapChart/handler';
import { selectSelectedTemplate, useReportStore } from '@/entities/report';
import type { HeaderChartProps } from './index';

/**
 * Backs the RR heat map header, exposing selection totals and the mark-all-reviewed checkbox. Derives the tri-state checkbox from each selected cell's reviewed status, sums the selected beat counts, applies bulk review marks by cell and mirrors them into beat options, and on a debounced selection change cancels in-flight requests, resets the bulk action and pagination and re-triggers the beat fetch.
 *
 * @param props - Header props carrying the activeButton beat type.
 * @returns An object carrying listSelectedCells, totalSelectedBeats, totalBeats, typeCheckbox and onClickCheckbox.
 */
const useHeaderChart = ({ activeButton }: HeaderChartProps) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const listSelectedCells = useReportStore((s) => s.listSelectedCells);
  const setListSelectedCells = useReportStore((s) => s.actions.setListSelectedCells);
  const setBulkActionAction = useReportStore((s) => s.actions.setBulkAction);
  /**
   * Sets the bulk action for the beats tab. Forwards the value to the store action with this tab's key bound in.
   *
   * @param v - New bulk action value or updater passed through to the store action.
   * @returns Nothing.
   */
  const setBulkAction = (v: Parameters<typeof setBulkActionAction>[1]) =>
    setBulkActionAction(keyRecoil, v);
  const setLoadingBeatStatus = useReportStore((s) => s.actions.setLoadingBeatStatus);
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Sets the beat list page index for the beats tab. Forwards the value to the store action with this tab's key bound in.
   *
   * @param v - New page index value or updater passed through to the store action.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const selectedTemplate = useReportStore(selectSelectedTemplate(keyRecoil));
  /**
   * Reads the currently selected morphology template for the beats tab straight off the report store. Applies the tab-keyed selector to a getState snapshot so callbacks see the live value without subscribing.
   *
   * @returns The selected template for this tab.
   */
  const getSelectedTemplate = () => selectSelectedTemplate(keyRecoil)(useReportStore.getState());
  const bulkReviewedBeats = useReportStore((s) => s.bulkReviewedBeats);
  const setBulkReviewedBeats = useReportStore((s) => s.actions.setBulkReviewedBeats);
  const resetBulkReviewedBeats = useReportStore((s) => s.resetBulkReviewedBeats);
  const setResetBulkReviewedBeats = useReportStore((s) => s.actions.setResetBulkReviewedBeats);
  const reviewedBeatsChanges = useReportStore((s) => s.reviewedBeatsChanges);
  const setReviewedBeatsChanges = useReportStore((s) => s.actions.setReviewedBeatsChanges);
  const isPreventLoadBeat = useRef(false);
  // False until the first non-empty cell selection has been processed, so the selection restored
  // from IndexedDB on load does not clear a rehydrated Page bulk action.
  const didHandleInitialSelection = useRef(false);
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Writes the beat options record for the beats tab. Forwards the value to the store action with this tab's key bound in.
   *
   * @param v - New beat options value or updater passed through to the store action.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  const setBulkActionByCellAction = useReportStore((s) => s.actions.setBulkActionByCell);
  /**
   * Sets the per-cell bulk action for the beats tab. Forwards the value to the store action with this tab's key bound in.
   *
   * @param v - New per-cell bulk action value or updater passed through to the store action.
   * @returns Nothing.
   */
  const setBulkActionByCell = (v: Parameters<typeof setBulkActionByCellAction>[1]) =>
    setBulkActionByCellAction(keyRecoil, v);
  const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
  /**
   * Resets the beats tab control modal state. Delegates to the store action with this tab's key bound in.
   *
   * @returns Nothing.
   */
  const resetControlModal = () => resetControlModalAction(keyRecoil);
  const isHideReviewedCell = useReportStore((s) => s.isHideReviewedCell);

  /**
   * Returns the beats tab bulk-action controls to their idle state. Sets both the bulk action and the per-cell bulk action to the nothing value and resets the control modal for this tab.
   *
   * @returns Nothing.
   */
  const resetBulkAction = () => {
    setBulkAction(BULK_ACTION_ENUM.NOTHING);
    setBulkActionByCell(BULK_ACTION_ENUM.NOTHING);
    resetControlModal();
  };

  const typeCheckbox = useMemo(() => {
    let isAllReviewed = true;
    let isReviewedAndNotReviewed = false;

    _.forEach(listSelectedCells, (item) => {
      const filterBeatChanges = _.filter(reviewedBeatsChanges, (x) => x.cellId === item.id);
      const { isReviewed } = calculateReviewedStatus({
        resetBulkReviewedBeats,
        item,
        filterBeatChanges,
        bulkReviewedBeats,
      });
      if (isReviewed) {
        isReviewedAndNotReviewed = true;
      } else {
        isAllReviewed = false;
      }
    });

    if (isAllReviewed) {
      return STATUS_CHECKBOX.CHECK;
    }

    if (isReviewedAndNotReviewed) {
      return STATUS_CHECKBOX.INDETERMINATE;
    }
    return STATUS_CHECKBOX.UNCHECK;
  }, [
    listSelectedCells,
    bulkReviewedBeats,
    resetBulkReviewedBeats,
    reviewedBeatsChanges,
    dataChartHeatMap,
  ]);

  const totalBeats = useMemo(() => {
    const result = listSelectedCells.reduce((sum, prev) => sum + prev.value, 0);
    return result;
  }, [listSelectedCells]);

  const totalSelectedBeats = listSelectedCells.length;

  /**
   * Marks every selected heat map cell reviewed or unreviewed and pushes the result into the report store. Clones the selection and the three pending review lists, mutates them by cell, writes each one back and mirrors them into beat options, then clears the selection when marking reviewed while reviewed cells are hidden.
   *
   * @param isReviewed - Destructured flag choosing whether the cells become reviewed or unreviewed.
   * @returns Nothing.
   */
  const onUpdateMarkAll = ({ isReviewed }: { isReviewed: boolean }) => {
    const currentListCells = _.cloneDeep(listSelectedCells);
    const cloneBulkReview = _.cloneDeep(bulkReviewedBeats);
    const cloneResetList = _.cloneDeep(resetBulkReviewedBeats);
    const cloneReviewBeatChanges = _.cloneDeep(reviewedBeatsChanges);
    handleMarkReviewByCellOnly({
      currentListCells,
      cloneBulkReview,
      cloneResetList,
      cloneReviewBeatChanges,
      isReviewed,
      activeButton,
    });
    setBulkReviewedBeats(cloneBulkReview);
    setResetBulkReviewedBeats(cloneResetList);
    setReviewedBeatsChanges(cloneReviewBeatChanges);
    setBeatOptions((prev: any) => ({
      ...prev,
      bulkReviewedBeatsState: cloneBulkReview,
      resetBulkReviewedBeatsState: cloneResetList,
      reviewedBeatsChangesState: cloneReviewBeatChanges,
    }));
    if (isReviewed) {
      if (isHideReviewedCell && listSelectedCells.length !== 0) {
        setListSelectedCells([]);
      }
    }
  };

  /**
   * Toggles the reviewed state of every selected heat map cell from the header checkbox. Unmarks all when the checkbox is fully checked and marks all otherwise, treating the indeterminate and unchecked states alike.
   *
   * @returns Nothing.
   */
  const onClickCheckbox = () => {
    switch (typeCheckbox) {
      case STATUS_CHECKBOX.CHECK: {
        onUpdateMarkAll({ isReviewed: false });
        break;
      }
      case STATUS_CHECKBOX.INDETERMINATE:
      case STATUS_CHECKBOX.UNCHECK: {
        onUpdateMarkAll({ isReviewed: true });
        break;
      }
      default:
        break;
    }
  };

  useEmitter(
    EMITTER_CONSTANTS.UPDATE_RELOAD_BEAT,
    (newListSelectedCells: any) => {
      if (listSelectedCells.length === 0 || newListSelectedCells?.length === 0) {
        return;
      }
      if (newListSelectedCells) {
        isPreventLoadBeat.current = true;
      }
      setBulkAction(BULK_ACTION_ENUM.NOTHING);
    },
    [listSelectedCells],
  );

  useDebounceEffect(() => {
    if (isPreventLoadBeat.current) {
      isPreventLoadBeat.current = false;
      return;
    }
    cancelAllAPI();
    // Skip clearing the armed bulk action on the first (restored) cell selection after load, so a
    // Page action rehydrated from IndexedDB survives. Every later user-driven selection change still
    // resets it. The arm is NOTHING before any restore, so skipping that first pass is a no-op there.
    if (didHandleInitialSelection.current) {
      resetBulkAction();
    } else if (listSelectedCells.length > 0) {
      didHandleInitialSelection.current = true;
    }
    setPageIndex((prev) => (prev.index === 0 ? prev : { index: 0 }));
    const selectedTemplate = getSelectedTemplate();
    setLoadingBeatStatus((prev) => {
      if (listSelectedCells.length === 0 && _.isEmpty(selectedTemplate)) {
        return prev.status === LOAD_BEAT_STATUS.NONE ? prev : { status: LOAD_BEAT_STATUS.NONE };
      }
      return { status: LOAD_BEAT_STATUS.FETCH };
    });
  }, [JSON.stringify(listSelectedCells), JSON.stringify(selectedTemplate)]);

  return {
    listSelectedCells,
    totalSelectedBeats,
    totalBeats,
    typeCheckbox,
    onClickCheckbox,
  };
};

export default useHeaderChart;
